import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@nextui-org/react';
import { CtyButton, CtyShowToast, CtySpinner, checkType } from '@circuitry-ai/doc-ui';
import { useGetEvaluationsPresignedUrlMutation, useTypedSelector } from '@circuitry-ai/doc-data';
import { parseDate } from '@internationalized/date';
import { useSearchParams } from 'react-router-dom';
import { useAnalystDashboardDateRange } from './analystDateRangeSession';

type EvaluationReportResponse = {
  status_code?: number;
  message?: string;
  data?:EvaluationReportData[]
};

type EvaluationReportData = {
  available_date_from?: string;
  available_date_to?: string;
  evaluation_id?: string;
  s3_key?: string;

}

const getEvaluationsBaseUrl = (): string => {
  const url = import.meta.env.CTY_EVALUATIONS_ANALYST_BASE_URL;
  return String(url ?? '').trim().replace(/\/$/, '');
};

export function EvaluationsDetails() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const analystType = searchParams.get('analystType') ?? '';
  const reportId = searchParams.get('reportId') ?? '';
  const reportType = searchParams.get('reportType') ?? '';
  const isEvaluationsAnalyst = analystType === 'evaluations';
  const isInteractiveReport = isEvaluationsAnalyst && reportType !== 'static';
  const isStaticReport = isEvaluationsAnalyst && reportType === 'static';
  const token = useTypedSelector((state) => state.auth.token);
  const { dateFrom, dateTo, setDateFrom, setDateTo, setDateRange } = useAnalystDashboardDateRange();
  const [isLoading, setIsLoading] = useState(false);
  const [presignedUrl, setPresignedUrl] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [availableDateRange, setAvailableDateRange] = useState<{ min: string; max: string } | null>(null);
  const [getEvaluationsPresignedUrl] = useGetEvaluationsPresignedUrlMutation();
  const hasAutoFetchedRef = useRef(false);

  const isDateRangeInvalid = !!dateFrom && !!dateTo && dateFrom.toString() > dateTo.toString();

  const setDateRangeFromAvailableDates = (evaluationData?: EvaluationReportData) => {
    const availableDateFrom = evaluationData?.available_date_from;
    const availableDateTo = evaluationData?.available_date_to;

    if (availableDateFrom && availableDateTo) {
      setDateRange(parseDate(availableDateFrom), parseDate(availableDateTo));
      setAvailableDateRange({ min: availableDateFrom, max: availableDateTo });
      return;
    }
  };

  const getEvaluationDetails = (payload: EvaluationReportResponse) => {
    const firstEvaluation = payload.data?.[0];
    return {
      evaluationId: firstEvaluation?.evaluation_id,
      s3Key: firstEvaluation?.s3_key,
      firstEvaluation,
    };
  };

  const throwEvaluationDataError = (payload: EvaluationReportResponse, firstEvaluation?: EvaluationReportData): never => {
    if (payload.status_code === 200) {
      setDateRangeFromAvailableDates(firstEvaluation);
      throw new Error(payload.message || t('Evaluation response is invalid'));
    }
    throw new Error(t('Evaluation response is invalid'));
  };

  const fetchEvaluationReport = async () => {
    setHasSubmitted(true);
    setErrorMessage('');

    if (isInteractiveReport && (!dateFrom || !dateTo)) {
      CtyShowToast(checkType.error, t('Please select both Date From and Date To'));
      return;
    }

    if (isInteractiveReport && dateFrom.toString() > dateTo.toString()) {
      CtyShowToast(checkType.error, t('Date From should be less than or equal to Date To'));
      return;
    }

    if (isStaticReport && !reportId) {
      CtyShowToast(checkType.error, t('Evaluation ID is missing'));
      return;
    }

    const baseUrl = getEvaluationsBaseUrl();
    if (!baseUrl) {
      CtyShowToast(checkType.error, t('Evaluations base URL is not configured'));
      return;
    }

    setIsLoading(true);
    try {
      const evaluationsEndpoint = `${baseUrl}/evaluations`;
      if (!token) {
        throw new Error(t('Authentication is not ready yet'));
      }
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      };

      const evaluationsResponse = await fetch(evaluationsEndpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(
          isStaticReport
            ? { evaluation_id: reportId }
            : {
              date_from: dateFrom.toString(),
              date_to: dateTo.toString(),
            }
        ),
      });

      if (!evaluationsResponse.ok) {
        throw new Error(t('Unable to fetch evaluation report'));
      }

      const evaluationPayload = (await evaluationsResponse.json()) as EvaluationReportResponse;
      const { evaluationId, s3Key, firstEvaluation } = getEvaluationDetails(evaluationPayload);

      if (!evaluationId || !s3Key) {
        throwEvaluationDataError(evaluationPayload, firstEvaluation);
        return;
      }

      const presignedPayload = await getEvaluationsPresignedUrl({
          evaluation_id: evaluationId,
          s3_key: s3Key,
      }).unwrap();
      const generatedUrl = presignedPayload?.data?.presigned_url;

      if (!generatedUrl) {
        throw new Error(t('Presigned URL is missing in response'));
      }

      setPresignedUrl(generatedUrl);
    } catch (error) {
      const message = (error as Error)?.message || t('Something went wrong while fetching evaluation report');
      setErrorMessage(message);
      CtyShowToast(checkType.error, message);
      setPresignedUrl('');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (hasAutoFetchedRef.current) return;
    hasAutoFetchedRef.current = true;
    fetchEvaluationReport();
    // Run once on mount to load report.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col gap-4">
      {isInteractiveReport && (
        <div className="flex flex-row flex-wrap gap-3 items-end">
          <DatePicker
            size="sm"
            label={t('Date Range')}
            labelPlacement="outside"
            value={dateFrom}
            minValue={availableDateRange ? parseDate(availableDateRange.min) : undefined}
            maxValue={availableDateRange ? parseDate(availableDateRange.max) : undefined}
            onChange={(date) => setDateFrom(date)}
            isInvalid={hasSubmitted && !dateFrom}
            errorMessage={hasSubmitted && !dateFrom ? t('Date From is required') : ''}
            className="w-full sm:w-[150px]"
          />
          <DatePicker
            size="sm"
            labelPlacement="outside"
            value={dateTo}
            minValue={availableDateRange ? parseDate(availableDateRange.min) : undefined}
            maxValue={availableDateRange ? parseDate(availableDateRange.max) : undefined}
            onChange={(date) => setDateTo(date)}
            isInvalid={(hasSubmitted && !dateTo) || isDateRangeInvalid}
            errorMessage={isDateRangeInvalid ? t('Date To should be greater than or equal to Date From') : (hasSubmitted && !dateTo ? t('Date To is required') : '')}
            className="w-full sm:w-[150px]"
          />
          <CtyButton
            id="fetch-evaluations-report"
            color="primary"
            className="w-full sm:w-[140px]"
            onPress={fetchEvaluationReport}
            isLoading={isLoading}
          >
            {t('View Report')}
          </CtyButton>
        </div>
      )}

      {isStaticReport && isLoading && (
        <div className="flex justify-center items-center h-[calc(100vh-140px)] min-h-[480px]">
          <CtySpinner size="lg" />
        </div>
      )}

      {!isLoading && errorMessage && (
        <p className="text-sm text-danger">{errorMessage}</p>
      )}

      {presignedUrl && (
        <div className="h-[calc(100vh-140px)] min-h-[480px]">
          <iframe
            src={presignedUrl}
            title={t('Evaluation Report')}
            className="w-full h-full border-0"
          />
        </div>
      )}

    </div>
  );
}

export default EvaluationsDetails;
